import { useState } from "react";
import { FlaskConical, Play } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { ASSIGNMENT_SIMULATE_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { DecisionTrail, Field, btnPrimary, inputCls } from "./shared";

/**
 * "Where would this lead go?" - customer/assignment/simulate/. Reads only:
 * no lead is assigned, round-robin turns are untouched. `draftRule` tests a
 * rule that is still being edited.
 */
const SAMPLE_FIELDS = ["lead_source", "lead_form", "state", "city", "project", "preferred_language",
                       "customer_type", "lead_score"];

export default function Simulator({ fields, triggers, draftRule, compact }) {
    const { create } = useApiMethods();
    const [mode, setMode] = useState("sample");
    const [leadId, setLeadId] = useState("");
    const [sample, setSample] = useState({});
    const [trigger, setTrigger] = useState("webhook_meta");
    const [result, setResult] = useState(null);
    const [running, setRunning] = useState(false);
    const byKey = Object.fromEntries((fields || []).map(f => [f.key, f]));

    const run = async () => {
        setRunning(true);
        try {
            const body = { trigger, ...(draftRule ? { draft_rule: draftRule } : {}) };
            if (mode === "lead") {
                if (!leadId) { ToastMsg("error", "Enter a lead ID"); return; }
                body.lead_id = leadId;
            } else body.lead = sample;
            const res = await create(ASSIGNMENT_SIMULATE_URL, body);
            if (res?.status === "success") setResult(res.result);
            else ToastMsg("error", res?.message || "Could not run the test");
        } finally { setRunning(false); }
    };

    return (
        <div className={cn("space-y-3", !compact && "rounded-xl border border-gray-200 dark:border-gray-700 p-4")}>
            {!compact && (
                <div className="flex items-center gap-1.5">
                    <FlaskConical className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Test where a lead would go</h3>
                </div>
            )}
            <div className="flex gap-3 text-xs">
                {[["sample", "Type in a lead"], ["lead", "Use an existing lead"]].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-1 cursor-pointer text-gray-700 dark:text-gray-200">
                        <input type="radio" checked={mode === k} onChange={() => setMode(k)} /> {l}
                    </label>
                ))}
            </div>

            {mode === "lead" ? (
                <Field label="Lead ID" help="The number in the lead's address bar (…?id=123).">
                    <input value={leadId} onChange={e => setLeadId(e.target.value.replace(/\D/g, ""))}
                        className={cn(inputCls, "max-w-[10rem]")} placeholder="e.g. 1234" />
                </Field>
            ) : (
                <div className={cn("grid gap-2", compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4")}>
                    {SAMPLE_FIELDS.filter(k => byKey[k]).map(k => {
                        const f = byKey[k];
                        return (
                            <Field key={k} label={f.label}>
                                {f.options ? (
                                    <select value={sample[k] || ""} onChange={e => setSample({ ...sample, [k]: e.target.value })}
                                        className={inputCls}>
                                        <option value="">—</option>
                                        {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                ) : (
                                    <>
                                        <input value={sample[k] || ""} list={`sim-${k}`}
                                            type={f.type === "number" ? "number" : "text"}
                                            onChange={e => setSample({ ...sample, [k]: e.target.value })}
                                            className={inputCls} />
                                        <datalist id={`sim-${k}`}>{(f.suggestions || []).map(s => <option key={s} value={s} />)}</datalist>
                                    </>
                                )}
                            </Field>
                        );
                    })}
                </div>
            )}

            <div className="flex flex-wrap items-end gap-2">
                <Field label="Arrives as" className="w-56">
                    <select value={trigger} onChange={e => setTrigger(e.target.value)} className={inputCls}>
                        {(triggers || []).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </Field>
                <button type="button" onClick={run} disabled={running} className={btnPrimary}>
                    <Play className="h-3.5 w-3.5" /> {running ? "Testing…" : "Run test"}
                </button>
            </div>

            {result && (
                <div className="rounded-lg bg-gray-50 dark:bg-gray-700/30 p-3 space-y-2">
                    {result.lead && (
                        <p className="text-[11px] text-gray-500">
                            Lead #{result.lead.id} {result.lead.name} — owner now: {result.lead.owner || "nobody"}
                        </p>
                    )}
                    <DecisionTrail decision={result} />
                    {result.live_note && <p className="text-[11px] text-amber-600">{result.live_note}</p>}
                    {!result.engine_on && <p className="text-[11px] text-amber-600">Automatic assignment is off, so this is what would happen once it is on.</p>}
                    <p className="text-[10px] text-gray-400">{result.note} Nothing was assigned.</p>
                </div>
            )}
        </div>
    );
}
